import React, { useCallback, useMemo } from 'react';
import type { ActionFn, SetIsLoading } from '#/common/components/DataGrid';
import DataGrid from '#/common/components/DataGrid';
import type { WorkOrderTemplate } from '#/graphql/types';
import columns from './columns';

import { actions } from './actions';
import { gql } from '@apollo/react-hooks';
import { getWorkOrderTemplateList } from '#/graphql/queries';
import useWOTemplateDelete from '../../hooks/useWOTemplateDelete';
import useToast from '#/common/hooks/useToast';

export interface WOTemplateDataGridProps {
  data: WorkOrderTemplate[];
}
const getWOTemplateListQuery = gql(getWorkOrderTemplateList);

const WOTemplateDataGrid: React.FC = () => {
  const { confirm } = useToast();
  const { deleteWOTemplate } = useWOTemplateDelete();
  const deleteItem = useCallback(
    async (
      ids: string[],
      { setIsLoading }: { setIsLoading?: SetIsLoading },
      hasPreventive: boolean,
    ) => {
      if (!ids) {
        return undefined;
      }

      let msg = `Deleting ${ids.length === 1 ? 'this template' : 'these templates'} is permanent. Are you sure you want to proceed?`;

      if (hasPreventive) {
        msg =
          ids.length === 1
            ? 'This Work Order Template is configured for preventive recurrence. Deleting it will stop all future Work Orders from being created. Existing Work Orders will not be affected. This action is permanent.'
            : 'These templates contain ones configured for preventive recurrence. Deleting them will stop all future Work Orders from being created. Existing Work Orders will not be affected. This action is permanent. Are you sure you want to proceed?';
      }
      const sure = await confirm('error', msg);
      if (!sure) {
        return;
      }
      setIsLoading && setIsLoading(true, 'Deleting data...');
      await deleteWOTemplate(ids.join(','));
      setIsLoading && setIsLoading(false);
    },
    [deleteWOTemplate],
  );

  const userSelectedActions: ActionFn<WorkOrderTemplate>[] = useMemo(
    () => [
      {
        key: 'delete-work-order-templates',
        icon: 'trash',
        text: 'Delete',
        onClick: async (e) => {
          const hasPreventive = e.selectedItems.some(
            (item) => item.category === 'preventive' && item.executionInterval?.length,
          );
          await deleteItem(
            e.selectedKeys.map((i: any) => i.id || i),
            { setIsLoading: e.setIsLoading },
            hasPreventive,
          );
          e.dg?.instance.clearSelection();
          e.dg?.instance.refresh();
        },
      },
    ],
    [deleteItem],
  );

  return (
    <DataGrid
      id="work-order-templates"
      pluralText="Work Order Templates"
      singularText="Work Order Template"
      columns={columns}
      actions={actions}
      getItemsQuery={getWOTemplateListQuery}
      selectedActions={userSelectedActions}
    />
  );
};

export default React.memo(WOTemplateDataGrid);
